import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "bootstrap";
import MainCarousel from "../components/MainCarousel";
import SearchBar from "../components/SearchBar";
import InstallPWA from "../components/InstallPWA";
import MobileInstallBanner from "../components/MobileInstallBanner";
import FavoriteRestaurants from "../components/FavoriteRestaurants";
import ReorderSection from "../components/ReorderSection";
import AllRestaurants from "../components/AllRestaurants";
import FoodCategoryStrip from "../components/FoodCategoryStrip";
import DiscountedFoods from "../components/DiscountedFoods";
import CitySelectPopup from "../popupPages/CitySelectPopup";
import AdresimPopup from "../popupPages/AdresimPopup";
import { isLoggedIn, getDefaultAddress, getUserToken, saveAddresses } from "../utils/tokenUtils";
import { getCustomerAddressList } from "../services/customerAddress";
import { useLanguage } from "../context/LanguageContext";

// Login olmuş ama adresi olmayan kullanıcıya "Adres Ekle" popup'ı açılır;
// kapatırsa bu süre boyunca tekrar sorulmaz.
const ADDRESS_PROMPT_SNOOZE_KEY = "addressPromptSnoozeUntil";
const ADDRESS_PROMPT_SNOOZE_MS = 10 * 60 * 1000;

function Dashboard() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());
  const [showCityPopup, setShowCityPopup] = useState(
    !isLoggedIn() && !localStorage.getItem("guestTownId")
  );
  const addressSavedRef = useRef(false);

  const defaultAddr = loggedIn ? getDefaultAddress() : null;
  const guestLocation = localStorage.getItem("guestLocationName");

  // "Beni Hatırla" auto-login sayfa yenilenmeden olur — misafir şehir popup'ı açık kalmasın
  useEffect(() => {
    const onLogin = () => {
      setLoggedIn(true);
      setShowCityPopup(false);
    };
    window.addEventListener("user-logged-in", onLogin);
    return () => window.removeEventListener("user-logged-in", onLogin);
  }, []);

  // Adresi yoksa ve erteleme süresi dolduysa Adres Ekle popup'ını aç
  useEffect(() => {
    if (!loggedIn) return;
    const token = getUserToken();
    if (!token) return;
    if (Date.now() < Number(localStorage.getItem(ADDRESS_PROMPT_SNOOZE_KEY) ?? 0)) return;

    let cancelled = false;
    getCustomerAddressList(token.userId)
      .then((res) => {
        if (cancelled) return;
        saveAddresses(res.data);
        if (res.data.length > 0) return;
        const el = document.getElementById("adresimModal");
        if (el) Modal.getOrCreateInstance(el).show();
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loggedIn]);

  // Kaydetmeden kapatılırsa 10 dk ertele
  useEffect(() => {
    if (!loggedIn) return;
    const el = document.getElementById("adresimModal");
    if (!el) return;
    const onHidden = () => {
      if (addressSavedRef.current) return;
      localStorage.setItem(ADDRESS_PROMPT_SNOOZE_KEY, String(Date.now() + ADDRESS_PROMPT_SNOOZE_MS));
    };
    el.addEventListener("hidden.bs.modal", onHidden);
    return () => el.removeEventListener("hidden.bs.modal", onHidden);
  }, [loggedIn]);

  const handleAddressSaved = () => {
    addressSavedRef.current = true;
    localStorage.removeItem(ADDRESS_PROMPT_SNOOZE_KEY);
    const token = getUserToken();
    if (!token) return;
    // Varsayılan adres değişti — navbar ve restoran listesi yeni ilçeye göre yenilensin
    getCustomerAddressList(token.userId)
      .then((res) => saveAddresses(res.data))
      .catch(() => {})
      .finally(() => window.location.reload());
  };

  const handleAddressClick = () => {
    if (loggedIn) {
      navigate("/adreslerim");
    } else {
      setShowCityPopup(true);
    }
  };

  return (
    <div>
      {/* ===== MOBİL LAYOUT ===== */}
      <div className="d-block d-sm-none">
        {/* Sticky kırmızı bar: adres + search */}
        <div className="mobile-sticky-header">
          <div className="mobile-address-bar" onClick={handleAddressClick}>
            <i className="bi bi-geo-alt-fill mobile-address-bar__icon"></i>
            <div className="flex-grow-1 overflow-hidden">
              <div className="mobile-address-bar__text">
                {loggedIn
                  ? (defaultAddr?.customerAddressDescription || t("navbar.selectAddress"))
                  : (guestLocation || t("navbar.selectLocation"))}
              </div>
              {defaultAddr && (
                <div className="mobile-address-bar__sub">
                  {defaultAddr.city?.cityNameTr} / {defaultAddr.town?.townNameTr}
                </div>
              )}
            </div>
            <i className="bi bi-chevron-right" style={{ color: "rgba(255,255,255,0.6)" }}></i>
          </div>
          <div className="px-3 pb-3">
            <div className="mobile-search-pill" onClick={() => navigate("/restaurants")}>
              <i className="bi bi-search mobile-search-pill__icon"></i>
              <span className="mobile-search-pill__text">{t("search.placeholder")}</span>
            </div>
          </div>
        </div>

        {/* Kırmızı promo alanı (scroll ile gider) */}
        <div className="mobile-hero-promo">
          <div className="mobile-promo-box">
            <div className="mobile-promo-box__title">{t("dashboard.promoTitle")}</div>
            <span
              className="mobile-promo-box__link"
              onClick={() => navigate("/restaurants")}
            >
              {t("dashboard.promoButton")} <i className="bi bi-chevron-right ms-1"></i>
            </span>
          </div>
        </div>

        {/* Beyaz içerik alanı */}
        <div className="mobile-content-area">
          <MainCarousel />
          <ReorderSection />
          <FavoriteRestaurants />

          <div className="px-3 pt-2 pb-1">
            <h6 className="fw-bold mb-2">
              <i className="bi bi-grid-3x3-gap-fill me-2 text-danger"></i>
              {t("categories.title")}
            </h6>
          </div>
          <div className="px-3">
            <FoodCategoryStrip />
          </div>

          <DiscountedFoods />

          <div className="px-3 pt-1 pb-1">
            <h6 className="fw-bold mb-2">
              <i className="bi bi-shop me-2 text-danger"></i>
              {t("dashboard.allRestaurants")}
            </h6>
          </div>
          <div className="px-3 pb-4">
            <AllRestaurants />
          </div>

          <MobileInstallBanner />
        </div>
      </div>

      {/* ===== MASAÜSTÜ LAYOUT ===== */}
      <div className="d-none d-sm-block">
        <SearchBar />
        <MainCarousel variant="desktop" />

        <div className="container">
          <h6 className="fw-bold mb-2 mt-4">
            <i className="bi bi-grid-3x3-gap-fill me-2 text-danger"></i>
            {t("categories.title")}
          </h6>
          <FoodCategoryStrip />

          <DiscountedFoods />
        </div>

        <FavoriteRestaurants />
        <InstallPWA />
      </div>

      {showCityPopup && <CitySelectPopup onClose={() => setShowCityPopup(false)} />}
      {loggedIn && <AdresimPopup address={null} defaultIsDefault onSuccess={handleAddressSaved} />}
    </div>
  );
}

export default Dashboard;
